import { apiService } from './api';
import { Exam, ExamGroupDetail, ExamGroupFormData, ExamGroupRow } from '../types';
import {
  MarksPrint,
  TranscriptPrint,
  MarksSheetDetail,
  MarksSheetPage,
  PaperEntry,
  PaperHistoryEntry,
  RecordOutcome,
  SheetStatus,
  StudentExams,
} from '../types/examination';

export interface ExamPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/** Where a sheet is: a subject of an exam row, for a section or the whole class. */
export interface SheetScope {
  examId: string;
  classId: string;
  sectionInstanceId?: string | null;
  subjectId: string;
}

/** Unlock target: a sheet (class, optionally one section) or named students. */
export type UnlockTarget = { classId: string; sectionInstanceId?: string | null } | { studentIds: string[] };

export const examService = {
  // ── Exam rows (pickers in reports, attendance sheets, bulk upload) ───────
  getExams: (params?: { page?: number; limit?: number; examTypeId?: string; sessionId?: string; classId?: string; status?: 'draft' | 'published'; search?: string }) =>
    apiService.get<{ exams: Exam[]; pagination: ExamPagination }>('/exams', params),

  getExamById: (id: string) => apiService.get<Exam>(`/exams/${id}`),

  // ── Exams, handled as groups (one exam across several classes) ───────────
  createExam: (data: ExamGroupFormData) => apiService.post<ExamGroupDetail>('/exams', data),

  getExamGroups: (params?: { page?: number; limit?: number; sessionId?: string; classId?: string; examTypeId?: string; status?: 'draft' | 'published'; search?: string }) =>
    apiService.get<{ groups: ExamGroupRow[]; pagination: ExamPagination }>('/exams/groups', params),

  getExamGroup: (groupId: string) => apiService.get<ExamGroupDetail>(`/exams/groups/${groupId}`),

  /** A draft takes any change; a published exam only those its recorded marks allow (409 lists the rest). */
  updateExamGroup: (groupId: string, data: ExamGroupFormData) => apiService.put<ExamGroupDetail>(`/exams/groups/${groupId}`, data),

  publishExamGroup: (groupId: string) => apiService.post<ExamGroupDetail>(`/exams/groups/${groupId}/publish`),

  unpublishExamGroup: (groupId: string) => apiService.post<ExamGroupDetail>(`/exams/groups/${groupId}/unpublish`),

  deleteExamGroup: (groupId: string) => apiService.delete(`/exams/groups/${groupId}`),

  // ── Marks sheets (Enter Marks) ────────────────────────────────────────────
  listSheets: async (params: { sessionId?: string; examGroupId?: string; classId?: string; subjectId?: string; status?: SheetStatus; search?: string; byClass?: boolean; page: number; limit: number }) =>
    (await apiService.get<MarksSheetPage>('/exams/marks/sheets', params)).data!,

  getSheet: async ({ examId, ...params }: SheetScope) =>
    (await apiService.get<MarksSheetDetail>(`/exams/${examId}/sheet`, { ...params, sectionInstanceId: params.sectionInstanceId ?? undefined })).data!,

  /** Record (or clear) papers; `lock` also submits them. */
  recordMarks: async (examId: string, entries: PaperEntry[], lock = false) =>
    (await apiService.post<RecordOutcome>(`/exams/${examId}/marks`, { entries, lock })).data!,

  /** One sheet with what its printed marks report shows — the browser draws the PDF. */
  getMarksPrint: async ({ examId, classId, sectionInstanceId, subjectId }: SheetScope) =>
    (await apiService.get<MarksPrint>('/examination/marks/print', { examId, classId, subjectId, sectionInstanceId: sectionInstanceId ?? undefined })).data!,

  unlockMarks: async (examId: string, subjectIds: string[], target: UnlockTarget, reason: string) =>
    (await apiService.post<{ unlocked: number }>(`/exams/${examId}/unlock`, { subjectIds, reason, ...target })).data!,

  // ── One student's papers (Results) ────────────────────────────────────────
  getStudentExams: async (studentId: string, classId: string, sessionId?: string) =>
    (await apiService.get<StudentExams>(`/examination/student-results/${studentId}/detailed`, { classId, sessionId })).data!,

  recordStudentMarks: async (studentId: string, examId: string, entries: Array<Omit<PaperEntry, 'studentId'>>) =>
    (await apiService.put<RecordOutcome>(`/examination/results/${studentId}`, { examId, entries })).data!,

  clearStudentMarks: async (studentId: string, examId: string) =>
    (await apiService.post<RecordOutcome>(`/examination/results/${studentId}/clear`, { examId })).data!,

  /** A student's transcript for their year in a class, to print. */
  getTranscript: async (studentId: string, classId: string, sessionId: string) =>
    (await apiService.get<TranscriptPrint>(`/examination/students/${studentId}/transcript`, { classId, sessionId })).data!,

  /** Several transcripts to print in one PDF (up to the server's cap), in print order. */
  printTranscripts: async (enrollmentIds: string[]) =>
    (await apiService.post<TranscriptPrint[]>('/examination/transcripts/print', { enrollmentIds })).data!,

  paperHistory: async (resultId: string, subjectId: string) =>
    (await apiService.get<PaperHistoryEntry[]>(`/examination/results/${resultId}/history`, { subjectId })).data ?? [],
};
